import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Menu,
  FormControl,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LanguageIcon from "@mui/icons-material/Language";
import { connect } from "react-redux";
import {
  getShareResultByResumeId as getShareResultByResumeIdAction,
  removeSharedAccess as removeSharedAccessAction,
  shareResult as shareResultAction,
} from "../redux/slices/resumeSlice";
import { enqueueSnackbar } from "notistack";

const ShareModal = ({
  open,
  onClose,
  title,
  resumeId,
  user,
  shareResult,
  type,
  getShareResultByResumeId,
  removeSharedAccess,
}) => {
  const [accessType, setAccessType] = useState("anyone");
  const [emails, setEmails] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [initialLoading, setInitialLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);

  // Fetch existing share data when modal opens
  useEffect(() => {
    const fetchShareData = async () => {
      if (open && resumeId) {
        try {
          setInitialLoading(true);
          const response = await getShareResultByResumeId({
            resume_score_id: resumeId,
            recruiter_id: user?.id,
          }).unwrap();

          if (response) {
            setShareData(response);

            // Set form state based on existing data
            setAccessType(response?.access_type || "anyone");

            // Extract emails from sharedWith array
            if (response?.shared_with && Array.isArray(response?.shared_with)) {
              const emailList = response?.shared_with
                ?.filter((item) => item.type === type)
                ?.map((item) => item.email);

              setEmails(emailList);
            }
          }
        } catch (error) {
          console.error("Failed to fetch share data:", error);
          // If not found, use defaults
          setAccessType("anyone");
          setEmails([]);
        } finally {
          setInitialLoading(false);
        }
      }
    };

    fetchShareData();
  }, [open, resumeId]);

  const handleAccessTypeChange = (event) => {
    setAccessType(event.target.value);
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter" && validateEmail(inputValue)) {
      addEmail(inputValue);
      e.preventDefault();
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const addEmail = (email) => {
    const normalized = email.toLowerCase();

    const isAlreadySharedWithCurrentType = shareData?.shared_with?.some(
      (item) => item.email.toLowerCase() === normalized && item.type === type
    );

    if (isAlreadySharedWithCurrentType) {
      enqueueSnackbar(`${email} already has access to this ${type} result`, {
        variant: "info",
      });
      setInputValue("");
      return;
    }

    // If not already in local state, add it
    if (
      email &&
      !emails.map((e) => e.toLowerCase()).includes(normalized) &&
      validateEmail(email)
    ) {
      setEmails([...emails, email]);
      setInputValue("");
    }
  };

  const removeEmail = async (emailToRemove) => {
    // If this is a newly added email (not yet in the database), just remove from state
    if (!shareData?.id) {
      setEmails(emails.filter((email) => email !== emailToRemove));
      return;
    }

    // Otherwise, call the API to remove access
    try {
      setRemoveLoading(true);

      await removeSharedAccess({
        resume_score_id: resumeId,
        recruiter_id: user?.id,
        email: emailToRemove,
      }).unwrap();

      // Update local state after successful API call
      setEmails(emails?.filter((email) => email !== emailToRemove));

      // Update share data
      const updatedSharedWith = shareData?.shared_with?.filter(
        (item) =>
          !(
            item.email.toLowerCase() === emailToRemove.toLowerCase() &&
            item.type === type
          )
      );

      setShareData({
        ...shareData,
        shared_with: updatedSharedWith,
      });

      enqueueSnackbar("Access removed successfully", { variant: "success" });
    } catch (error) {
      console.error("Failed to remove access:", error);
      enqueueSnackbar("Failed to remove access", { variant: "error" });
    } finally {
      setRemoveLoading(false);
    }
  };
  const handleMenuOpen = (event, email) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedEmail(email);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedEmail(null);
  };

  const handleRemove = () => {
    if (selectedEmail) {
      removeEmail(selectedEmail);
    }
    handleMenuClose();
  };

  const copyLinkToClipboard = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 2000);

      enqueueSnackbar("Link copied", { variant: "success" });
    } catch (error) {
      console.error("Failed to copy link:", error);
      enqueueSnackbar("Failed to copy link", { variant: "error" });
    }
  };

  const handleSend = async () => {
    try {
      if (
        accessType === "anyone" &&
        type === "resume" &&
        shareData?.public_url
      ) {
        console.log("enter here");
        await copyLinkToClipboard(shareData.public_url);
        return;
      }

      if (
        accessType === "anyone" &&
        type === "interview" &&
        shareData?.public_url_interview_result
      ) {
        console.log("enter here to copy interview ");
        await copyLinkToClipboard(shareData.public_url_interview_result);
        return;
      }

      // Check if emails are already shared with current type
      const alreadySharedEmailsWithType = new Set(
        (shareData?.shared_with || [])
          .filter((item) => item.type === type)
          .map((item) => item.email.toLowerCase())
      );

      const newEmails = emails.filter(
        (email) => !alreadySharedEmailsWithType.has(email.toLowerCase())
      );

      if (accessType === "restricted" && newEmails.length === 0) {
        enqueueSnackbar("Result is already shared with these recruiters", {
          variant: "info",
        });
        return;
      }

      setLoading(true);

      const payload = {
        resume_score_id: resumeId,
        recruiter_id: user?.id,
        company_id: user?.company_id,
        emails: emails,
        accessType: accessType,
        candidate_name: title,
        type: type,
      };

      const response = await shareResult(payload).unwrap();
      console.log("response in handle send", response);
      if (accessType === "anyone" && type === "resume") {
        const shareableLink = response?.public_url;

        if (shareableLink) {
          await copyLinkToClipboard(shareableLink);
          onClose();
        }
      } else if (accessType === "anyone" && type === "interview") {
        const shareableLink = response?.public_url_interview_result;
        if (shareableLink) {
          await copyLinkToClipboard(shareableLink);
          onClose();
        }
      } else {
        setAccessType("anyone");
        setEmails([]);
        setInputValue("");
        enqueueSnackbar("Result shared successfully", { variant: "success" });
        onClose();
      }
    } catch (error) {
      console.error("Failed to share result:", error);
      // Optionally show toast/snackbar
    } finally {
      setLoading(false);
    }
  };

  // Effect to handle chip rendering when inputValue changes
  useEffect(() => {
    if (inputValue.endsWith(" ") && validateEmail(inputValue.trim())) {
      addEmail(inputValue.trim());
    }
  }, [inputValue]);

  if (initialLoading) {
    return (
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: 500,
            height: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        }}
      >
        <CircularProgress size={40} />
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxWidth: 500,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Share "{title} Result"
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            border: "1px solid #454545",
          }}
        >
          <CloseIcon sx={{ fontSize: 12, color: "#2d2d2d" }} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {accessType === "restricted" && (
          <Box sx={{ my: 2 }}>
            <TextField
              fullWidth
              placeholder="Add emails to share the results"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              InputProps={{
                sx: {
                  fontFamily: "Outfit",
                  fontSize: 14,
                  mb: 2,
                },
              }}
              InputLabelProps={{
                sx: {
                  fontFamily: "Outfit",
                },
              }}
            />
          </Box>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1, color: "#151515" }}>
          Who has access
        </Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <Select
            value={accessType}
            onChange={handleAccessTypeChange}
            displayEmpty
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              backgroundColor: "transparent", // Optional
              boxShadow: "none", // Optional
            }}
            renderValue={(selected) => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "#5A5A64",
                  fontSize: 14,
                  fontFamily: "Outfit",
                }}
              >
                {selected === "anyone" ? (
                  <>
                    <LanguageIcon
                      sx={{ mr: 1, fontSize: 18, color: "#425259" }}
                    />
                    Anyone with the link
                  </>
                ) : (
                  <>
                    <LockIcon sx={{ mr: 1, fontSize: 18, color: "#425259" }} />
                    Restricted
                  </>
                )}
              </Box>
            )}
          >
            <MenuItem value="anyone">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "#5A5A64",
                  fontSize: 14,
                  fontFamily: "Outfit",
                }}
              >
                <LanguageIcon sx={{ mr: 1, fontSize: 18, color: "#425259" }} />
                Anyone with the link
              </Box>
            </MenuItem>
            <MenuItem value="restricted">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "#5A5A64",
                  fontSize: 14,
                  fontFamily: "Outfit",
                }}
              >
                <LockIcon sx={{ mr: 1, fontSize: 18, color: "#425259" }} />
                Restricted
              </Box>
            </MenuItem>
          </Select>
        </FormControl>

        {emails.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              People with access
            </Typography>
            <List
              sx={{
                p: 0,
                maxHeight: "100px",
                overflow: "auto",
              }}
            >
              {emails?.map((email) => (
                <ListItem key={email} sx={{ px: 0, py: 0.5 }}>
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: "#4DD0E1" }}>
                      {email.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={email}
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={(e) => handleMenuOpen(e, email)}
                      size="small"
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem
                sx={{
                  color: "#5A5A64",
                  fontSize: 14,
                  fontFamily: "Outfit",
                  px: 2,
                  py: 1.5,
                }}
                onClick={handleRemove}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                  }}
                >
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      border: "1.5px solid red",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 12, color: "red" }} />
                  </Box>
                  <Box sx={{ fontSize: 14, fontFamily: "Outfit" }}>Remove</Box>
                </Box>
              </MenuItem>
            </Menu>
          </>
        )}

        {emails.length === 0 && (
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Avatar
              sx={{ width: 32, height: 32, mr: 1, bgcolor: "#397cff" }}
            ></Avatar>
            <Typography
              variant="body2"
              color="text.secondary"
              fontSize="14px"
              fontFamily="Outfit"
            >
              No person have access
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mt: 2,
            gap: 1,
          }}
        >
          {/* Cancel button appears only for restricted access with emails */}
          {accessType === "restricted" && emails.length > 0 && (
            <Button
              variant="outlined"
              onClick={() => {
                setAccessType("anyone");
                setEmails([]);
                setInputValue("");
                onClose();
              }}
              sx={{
                borderRadius: "20px",
                textTransform: "none",
                fontSize: "14px",
                fontFamily: "Outfit",
                px: 3,
              }}
            >
              Cancel
            </Button>
          )}

          {/* Main action button */}
          <Button
            variant="contained"
            onClick={
              accessType === "anyone"
                ? handleSend
                : emails.length > 0
                ? handleSend
                : onClose
            }
            disabled={
              loading || (accessType === "restricted" && emails.length === 0)
            }
            sx={{
              borderRadius: "20px",
              backgroundColor: "#4DD0E1",
              "&:hover": {
                backgroundColor: "#26C6DA",
              },
              fontSize: "14px",
              fontFamily: "Outfit",
              textTransform: "none",
              px: 3,
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : copied ? (
              "Copied!"
            ) : accessType === "anyone" ? (
              "Copy link"
            ) : emails.length > 0 ? (
              "Sent"
            ) : (
              "Done"
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
});

const mapDipatchToProps = {
  shareResult: shareResultAction,
  getShareResultByResumeId: getShareResultByResumeIdAction,
  removeSharedAccess: removeSharedAccessAction,
};

/* Export
============================================================================= */
export default connect(mapStateToProps, mapDipatchToProps)(ShareModal);
